import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getNoticiasPublic } from "services/publicNoticiasService";
import { getConfig } from "config/config";
import { usePageLoading } from "hooks/usePageLoading";

// Sección PÚBLICA de noticias (sin login): muestra las noticias cargadas, más
// nuevas primero, con scroll infinito de a 5. Cada noticia es una card con
// miniatura (si hay imagen), título y un botón "Ver más" que abre el detalle.
// Estética hermana de la de torneos (.tpub) pero con overlay rosa (.npub).

const PAGE_SIZE = 5;
const config = getConfig();
const imagenUrl = (filename) => (filename ? config.filesUrl + "/noticias/" + filename : "");

const formatFecha = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("es-AR", { day: "numeric", month: "long", year: "numeric" });
};

export default function NoticiasPublico(props) {
  const doneLoading = usePageLoading(props);
  const [items, setItems] = useState([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [firstDone, setFirstDone] = useState(false);
  const navigate = useNavigate();
  const sentinelRef = useRef(null);
  const loadingRef = useRef(false); // evita disparos solapados del observer

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore) return;
    loadingRef.current = true;
    setLoading(true);
    const data = await getNoticiasPublic(skip, PAGE_SIZE);
    if (data === "NETWORK ERROR" || !data) {
      setError(true);
      setHasMore(false);
    } else {
      const nuevas = Array.isArray(data.records) ? data.records : [];
      setItems((prev) => [...prev, ...nuevas]);
      setSkip((prev) => prev + nuevas.length);
      setHasMore(!!data.hasMore);
    }
    setLoading(false);
    setFirstDone(true);
    loadingRef.current = false;
  }, [skip, hasMore]);

  // Primera carga. Sólo esta tanda bloquea con el preloader: las siguientes
  // las dispara el scroll infinito y se cargan en segundo plano.
  useEffect(() => {
    loadMore().then(doneLoading, doneLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Observer del centinela: cuando entra en viewport, carga el próximo grupo.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "200px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore]);

  const verMas = (id) => navigate("/noticias/" + id);

  return (
    <div className="npub">
      <div className="tpub-shell">
        <div className="tpub-head">
          <div className="tpub-head-text">
            <div className="tpub-title">Noticias</div>
          </div>
        </div>

        {firstDone && items.length === 0 && !error && (
          <p className="tpub-empty">Todavía no hay noticias.</p>
        )}
        {error && items.length === 0 && (
          <p className="tpub-empty">No se pudieron cargar las noticias.</p>
        )}

        {items.map((n) => (
          <div className="npub-card" key={n._id}>
            {imagenUrl(n.imagen) && (
              <div className="npub-thumb">
                <img src={imagenUrl(n.imagen)} alt={n.titulo} />
              </div>
            )}
            <div className="npub-card-body">
              <div className="npub-fecha">{formatFecha(n.fecha)}</div>
              <div className="npub-card-title">{n.titulo}</div>
              <button type="button" className="npub-vermas" onClick={() => verMas(n._id)}>
                Ver más
              </button>
            </div>
          </div>
        ))}

        {loading && <p className="tpub-loading">Cargando…</p>}

        {/* Centinela para el scroll infinito */}
        <div ref={sentinelRef} style={{ height: 1 }} />
      </div>
    </div>
  );
}
